import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, LogOut, User } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { NAV, ACCOUNT } from "@/constants/testIds";
import LanguageSwitcher from "./LanguageSwitcher";
import CartDrawer from "./CartDrawer";

const Header = () => {
  const { user, logout, cartCount } = useApp();
  const [cartOpen, setCartOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const nav = useNavigate();

  const linkCls = ({ isActive }) =>
    `text-sm tracking-label uppercase font-medium link-underline pb-1 ${
      isActive ? "text-primary" : "text-foreground/70 hover:text-foreground"
    }`;

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-border backdrop-blur-xl bg-background/85">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2 group">
            <span className="text-secondary text-2xl">✦</span>
            <span className="font-serif text-2xl tracking-tight">AstroSamagri</span>
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            <NavLink to="/" end className={linkCls} data-testid={NAV.homeLink}>Home</NavLink>
            <NavLink to="/predictions" className={linkCls} data-testid={NAV.predictionsLink}>Predictions</NavLink>
            <NavLink to="/store" className={linkCls} data-testid={NAV.storeLink}>Store</NavLink>
            <NavLink to="/consult" className={linkCls}>Consult</NavLink>
          </nav>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <button
              onClick={() => setCartOpen(true)}
              data-testid={NAV.cartButton}
              className="relative p-2 rounded-sm border border-border hover:border-primary luxe-btn"
              aria-label="Open cart"
            >
              <ShoppingBag className="w-4 h-4" strokeWidth={1.5} />
              {cartCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-primary text-primary-foreground text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
            {user ? (
              <div className="hidden sm:flex items-center gap-2">
                <Link
                  to="/account"
                  data-testid={ACCOUNT.accountLink}
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-label px-3 py-2 border border-border hover:bg-muted luxe-btn"
                >
                  <User className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {user.name.split(" ")[0]}
                </Link>
                <button
                  onClick={() => { logout(); nav("/"); }}
                  data-testid={NAV.logoutButton}
                  className="p-2 border border-border hover:bg-muted luxe-btn"
                  aria-label="Sign out"
                >
                  <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/login" data-testid={NAV.loginLink} className="text-xs uppercase tracking-label px-3 py-2 hover:text-primary">Login</Link>
                <Link to="/register" data-testid={NAV.registerLink} className="text-xs uppercase tracking-label px-3 py-2 bg-primary text-primary-foreground hover:bg-primary/90 luxe-btn">Sign Up</Link>
              </div>
            )}
            <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} aria-label="menu">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-border bg-background px-6 py-4 space-y-3">
            <NavLink to="/" end className={linkCls} onClick={() => setMobileOpen(false)}>Home</NavLink>
            <br />
            <NavLink to="/predictions" className={linkCls} onClick={() => setMobileOpen(false)}>Predictions</NavLink>
            <br />
            <NavLink to="/store" className={linkCls} onClick={() => setMobileOpen(false)}>Store</NavLink>
            <br />
            <NavLink to="/consult" className={linkCls} onClick={() => setMobileOpen(false)}>Consult</NavLink>
            {user && (
              <>
                <br />
                <NavLink to="/account" className={linkCls} onClick={() => setMobileOpen(false)}>My Account</NavLink>
              </>
            )}
            {!user && (
              <div className="pt-3 flex gap-3">
                <Link to="/login" className="text-xs uppercase tracking-label px-3 py-2 border">Login</Link>
                <Link to="/register" className="text-xs uppercase tracking-label px-3 py-2 bg-primary text-primary-foreground">Sign Up</Link>
              </div>
            )}
          </div>
        )}
      </header>
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </>
  );
};

export default Header;
